import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useToast } from '../lib/toast'
import { Button, ButtonLink, Chip, EmptyState, Field, FormError, Label, PageTitle, Spinner } from '../components/ui'
import { type Media, api, call, messageOf, uploadMedia } from '../lib/api'
import { IosAppLink } from '../lib/app'
import { PhotoCropper } from '../components/PhotoCropper'
import { withOthers } from '../lib/cats'
import { leagueQuery, meQuery, petsQuery } from '../lib/queries'
import { report } from '../lib/report'

const PICKING = 'fcl-picking' // sessionStorage: when the photo picker was opened
const MAX_UPLOAD = 100 << 20 // mirrors media.MaxUploadBytes
const CROP_OUT = 2048 // the largest side of a cropped post photo
/** Clears the "picker is open" mark and says how long it had been open. */
const forget = (): number => {
  try {
    const opened = Number(sessionStorage.getItem(PICKING) ?? 0)
    sessionStorage.removeItem(PICKING)
    return opened ? Math.round((Date.now() - opened) / 1000) : 0
  } catch {
    return 0 // private mode
  }
}

// The web posts photos only (D39). On an iPhone the picker can still fail to
// hand a file over, and reports that exactly as it reports Cancel: no file, no
// error. After a cancel that took a while, say what may have happened.
const SLOW_CANCEL_S = 6
const GAVE_UP = "Nothing came back from your phone. If you did pick a photo, try once more; if it keeps happening, it may still be downloading from iCloud."
const VIDEO = 'video'

// Read once, when this screen's code loads. After a reload that is the page
// load itself, which is the moment we want to know about.
const lostNotice = (() => {
  try {
    const opened = Number(sessionStorage.getItem(PICKING) ?? 0)
    sessionStorage.removeItem(PICKING)
    const age = (Date.now() - opened) / 1000
    if (!opened || age > 600) return undefined
    report('picker_lost', { age_s: Math.round(age) })
    return 'Your phone closed this page while it was getting that photo ready, so it never arrived. Pick it again.'
  } catch {
    return undefined // no sessionStorage: nothing to recover
  }
})()

export const Route = createFileRoute('/l/$slug/post')({
  validateSearch: (s: Record<string, unknown>): { category?: number } => ({ category: typeof s.category === 'number' ? s.category : undefined }),
  component: Post,
})

function Post() {
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const { data: league } = useSuspenseQuery(leagueQuery(slug))
  const pets = useQuery(petsQuery)
  const meID = useQuery(meQuery).data?.user.id
  const round = league.current_round
  const cats = round?.categories ?? []

  const [categoryId, setCategoryId] = useState(search.category ?? cats[0]?.id)
  const [pickedPets, setPetIds] = useState<number[]>()
  const [newCat, setNewCat] = useState('')
  const [caption, setCaption] = useState('')
  const [file, setFile] = useState<File>()
  // What was picked, so cropping again starts from the whole photo.
  const [original, setOriginal] = useState<File>()
  const [cropping, setCropping] = useState(false)
  const latest = useRef(0) // the upload whose result counts; an older one finishing late is ignored
  const [preview, setPreview] = useState<string>()
  const [progress, setProgress] = useState(0)
  const [media, setMedia] = useState<Media>()
  const [uploadError, setUploadError] = useState<string>()
  const [previewBroken, setPreviewBroken] = useState(false)
  const [notice, setNotice] = useState<string | undefined>(lostNotice)
  const [hint, setHint] = useState<string>()
  const picker = useRef<HTMLInputElement>(null)

  // iOS can throw this page away while Photos is getting a file ready, then
  // reload it: the person lands back here with nothing chosen and no idea why.
  // Remember that the picker was open, so a fresh page can tell it was
  // interrupted, say so, and report it.
  useEffect(() => {
    const el = picker.current
    const cancelled = () => {
      const open_s = forget()
      report('picker_cancel', { open_s })
      if (open_s >= SLOW_CANCEL_S) setHint(GAVE_UP)
    }
    el?.addEventListener('cancel', cancelled)
    return () => el?.removeEventListener('cancel', cancelled)
  }, [])
  const openPicker = () => {
    try {
      sessionStorage.setItem(PICKING, String(Date.now()))
    } catch {
      /* private mode */
    }
    report('picker_open')
    setHint(undefined)
    picker.current?.click()
  }

  // Default to the first cat until the person picks. A post can carry
  // several cats, in the order they were tapped; at least one stays picked.
  const first = pets.data?.[0]?.id
  const petIds = pickedPets ?? (first === undefined ? [] : [first])
  const toggle = (id: number) =>
    setPetIds(petIds.includes(id) ? (petIds.length > 1 ? petIds.filter((x) => x !== id) : petIds) : [...petIds, id])
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  // The upload starts the moment a file is picked, while the person is still
  // choosing a cat and writing a caption.
  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const open_s = forget()
    setHint(undefined)
    const f = e.target.files?.[0]
    e.target.value = '' // so picking the same file again still fires
    if (!f || f.size === 0) {
      report('picker_empty', { files: e.target.files?.length ?? 0, open_s })
      setNotice("Your phone didn't hand over that file. Pick it again.")
      return
    }
    const mb = Math.round(f.size / (1 << 20))
    report('picker_change', { mb, type: f.type || 'unknown', open_s })
    setNotice(undefined)
    if (f.type.startsWith('video/') || /\.(mov|mp4|m4v|webm)$/i.test(f.name)) {
      setNotice(VIDEO)
      return
    }
    if (f.size > MAX_UPLOAD) {
      // Don't spend minutes uploading something the server will refuse.
      setNotice(`That photo is ${mb} MB and the limit is ${MAX_UPLOAD >> 20} MB.`)
      return
    }
    setOriginal(f)
    await upload(f)
  }

  // A crop replaces the upload: the cropped file goes up in its place.
  const upload = async (f: File) => {
    const mb = Math.round(f.size / (1 << 20))
    setFile(f)
    setMedia(undefined)
    setUploadError(undefined)
    setProgress(0)
    setPreviewBroken(false)
    setPreview(URL.createObjectURL(f))
    const n = ++latest.current
    try {
      const m = await uploadMedia(f, (p) => n === latest.current && setProgress(p))
      if (n === latest.current) setMedia(m)
    } catch (err) {
      if (n !== latest.current) return
      report('upload_failed', { mb, message: messageOf(err).slice(0, 160) })
      setUploadError(messageOf(err))
    }
  }

  // Some browsers can't draw what they can upload: Chrome has no HEIC
  // decoder, so an iPhone photo picked on a Mac previews as a broken image.
  // The server converts everything, so poll until its version is ready and
  // show that instead. Until then, a placeholder, never a broken image.
  const processed = useQuery({
    queryKey: ['media', media?.id],
    queryFn: () => call(api.GET('/media/{id}', { params: { path: { id: media!.id } } })),
    enabled: !!media && media.status === 'processing',
    refetchInterval: (q) => (q.state.data?.status === 'processing' || !q.state.data ? 1000 : false),
  })
  const current = processed.data ?? media
  const failed = current?.status === 'failed'

  const post = useMutation({
    mutationFn: async () => {
      let ids = petIds
      if (ids.length === 0) {
        const pet = await call(api.POST('/pets', { body: { name: newCat } }))
        ids = [pet.id]
      }
      return call(api.POST('/categories/{id}/submissions', { params: { path: { id: categoryId! } }, body: { media_id: media!.id, pet_ids: ids, caption } }))
    },
    onSuccess: () => {
      const name = cats.find((c) => c.id === categoryId)?.name ?? 'the round'
      qc.invalidateQueries({ queryKey: ['feed', categoryId] })
      qc.invalidateQueries({ queryKey: ['league', slug] })
      qc.invalidateQueries({ queryKey: petsQuery.queryKey })
      toast(`Posted to ${name}`)
      navigate({ to: '/l/$slug', params: { slug }, search: { category: categoryId } })
    },
  })

  if (!round || round.status !== 'submitting') {
    return (
      <EmptyState title="Submissions are closed" action={<ButtonLink to="/l/$slug" params={{ slug }} search={{ category: undefined }} size="sm">Back to this week</ButtonLink>}>
        The next round opens when this one goes to voting.
      </EmptyState>
    )
  }

  const noCats = pets.isSuccess && pets.data.length === 0
  const ready = !!media && !failed && !!categoryId && (petIds.length > 0 || newCat.trim().length > 0)
  const uploading = !media && !uploadError && (
    <div className="absolute inset-x-0 bottom-0 bg-black/55 px-3 py-2 text-[12.5px] text-white">
      <div className="mb-1.5 h-1.5 overflow-hidden rounded-full bg-white/25"><div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} /></div>
      {progress < 1 ? `Uploading… ${Math.round(progress * 100)}%` : 'Almost there…'}
    </div>
  )
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (ready) post.mutate()
  }

  return (
    <form onSubmit={submit} className="grid gap-5">
      <PageTitle eyebrow={`Round ${round.number}`}>Post a photo</PageTitle>

      <input ref={picker} id="post-file" type="file" accept="image/*,.heic,.heif" className="sr-only" onChange={pick} />
      <FormError>{notice === VIDEO ? <>Video posts are handled in <IosAppLink />. Pick a photo here.</> : notice}</FormError>
      {hint && !file && <p className="rounded-card border border-line bg-surface px-4 py-3 text-[14px] text-muted">{hint}</p>}
      {!file ? (
        <button type="button" onClick={openPicker} className="grid aspect-photo w-full place-items-center rounded-card border-[1.5px] border-dashed border-line bg-surface text-center">
          <span className="grid gap-1.5 px-6">
            <b className="text-[17px] font-bold">Choose a photo</b>
            <span className="text-[14px] text-muted">Take one now or pick it from your library.</span>
          </span>
        </button>
      ) : (
        <div className="grid gap-2">
          <div className="relative overflow-hidden rounded-card bg-sunken">
            {current?.status === 'ready' ? (
              <img src={current.display_url ?? undefined} alt="Your upload" className="aspect-square w-full object-cover" />
            ) : previewBroken ? (
              // Chrome has no HEIC decoder: wait for the server's version, never a broken image.
              <div className="grid aspect-photo place-items-center px-6 text-center text-[14px] text-muted">
                <span className="grid justify-items-center gap-2"><Spinner />Getting your photo ready…</span>
              </div>
            ) : (
              <img src={preview} alt="Your upload" className="aspect-square w-full object-cover" onError={() => setPreviewBroken(true)} />
            )}
            {uploading}
          </div>
          {/* The feed shows every photo as a centre-cropped square (D58): preview that, not the whole photo. */}
          <p className="text-[13px] text-muted">{previewBroken ? 'The feed will show a square from the middle of it.' : 'This square is what the feed shows. Crop to choose which part.'}</p>
          <FormError>{uploadError ?? (failed ? (current?.error ?? "We couldn't read that file. Try a different photo.") : undefined)}</FormError>
          <div className="flex flex-wrap gap-2">
            {uploadError && file && (
              <Button size="sm" onClick={() => upload(file)} data-retry>
                Try again
              </Button>
            )}
            {!previewBroken && (
              <Button size="sm" onClick={() => setCropping(true)} data-crop>
                Crop
              </Button>
            )}
            <Button size="sm" onClick={openPicker}>
              Choose a different one
            </Button>
          </div>
          {cropping && original && (
            <PhotoCropper
              file={original}
              round={false}
              out={CROP_OUT}
              label="Crop the photo"
              onCancel={() => setCropping(false)}
              onDone={(f) => {
                setCropping(false)
                if (f !== original) upload(f)
              }}
            />
          )}
        </div>
      )}

      <fieldset className="grid gap-2">
        <Label>Category</Label>
        <div className="flex flex-wrap gap-1.5">
          {cats.map((c) => (
            <button key={c.id} type="button" aria-pressed={c.id === categoryId} onClick={() => setCategoryId(c.id)}>
              <Chip tone={c.id === categoryId ? 'selected' : 'default'}>{c.name}</Chip>
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="grid gap-2">
        <Label>{(pets.data?.length ?? 0) > 1 ? 'Which cats?' : 'Which cat?'}</Label>
        {noCats ? (
          <Field label="Your cat's name" required maxLength={40} value={newCat} onChange={(e) => setNewCat(e.target.value)} help="You can add more cats later from your account." />
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {(pets.data ?? []).map((p) => {
              const shared = withOthers(p, meID)
              return (
                <button key={p.id} type="button" aria-pressed={petIds.includes(p.id)} onClick={() => toggle(p.id)}>
                  <Chip tone={petIds.includes(p.id) ? 'selected' : 'default'}>{p.name}{shared && <span className="font-normal opacity-75">· {shared}</span>}</Chip>
                </button>
              )
            })}
          </div>
        )}
        {(pets.data ?? []).flatMap((p) => (p.merge_suggestions ?? []).filter((s) => s.direction === 'in' && s.suggested_by.id !== meID).map((s) => (
          <p key={`${p.id}-${s.other.id}`} className="text-[13px] text-muted">
            {s.suggested_by.display_name} says {p.name} is the same cat as {s.other.name}.{' '}
            <Link to="/account" className="font-semibold text-accent-ink underline">Look in your account</Link>
          </p>
        )))}
      </fieldset>

      <Field label="Caption" maxLength={140} placeholder="Optional" value={caption} onChange={(e) => setCaption(e.target.value)} />

      <FormError>{post.isError && messageOf(post.error)}</FormError>
      <p className="text-[13px] text-muted">Only post what you made or may share.</p>
      <div className="grid grid-cols-[auto_1fr] gap-2.5">
        <ButtonLink to="/l/$slug" params={{ slug }} search={{ category: categoryId }}>
          Cancel
        </ButtonLink>
        <Button type="submit" variant="primary" disabled={!ready} busy={post.isPending}>
          {file && !media && !uploadError ? 'Uploading…' : 'Post it'}
        </Button>
      </div>
    </form>
  )
}
